import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {jwtDecode, JwtPayload} from 'jwt-decode';

@Injectable({
  providedIn: 'root'
})
export class TokenService {

  constructor(private router: Router) { }

  /**
   * Saves the token and extracts user information.
   * @param token The JWT token received after login.
   */
  saveToken(token: string): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('token', token);
      
      try {
        const decodedToken: any = jwtDecode(token);
        localStorage.setItem('is_admin', decodedToken.is_admin);
        localStorage.setItem('roles', JSON.stringify(decodedToken.roles));
      } catch (error) {
        // In case of a decoding error (invalid or corrupted token), clean everything up.
        console.error('Token decoding error:', error);
        this.clearToken();
      }
    }
    this.router.navigate(['/']);
  }

  /**
   * Checks if the token is valid and not expired.
   * @returns True if the token is valid, false otherwise.
   */
  isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    try {
      const decodedToken: JwtPayload = jwtDecode(token);
      if (decodedToken.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        return decodedToken.exp > currentTime;
      }
      return false;
    } catch (error) {
      console.error('Invalid token format:', error);
      return false;
    }
  }

  /**
   * Checks if a token exists and the user is considered logged in.
   * @returns True if a token is present.
   */
  isLogged(): boolean {
    if (typeof localStorage !== 'undefined') {
      return !!localStorage.getItem('token');
    } else {
      return false;
    }
  }

  /**
   * Checks if the user is a platform super-admin.
   * @returns True if the user has the admin status.
   */
  isAdmin(): boolean {
    if (typeof localStorage !== 'undefined') {
      const isAdmin = localStorage.getItem('is_admin');
      return isAdmin === 'true';
    }
    return false;
  }

  /**
   * Checks if the user has a specific role.
   * @param role The name of the role to check.
   * @returns True if the role is in the user's role list.
   */
  hasRole(role: string): boolean {
    if (typeof localStorage !== 'undefined') {
      const rolesString = localStorage.getItem('roles');
      if (!rolesString) {
        return false;
      }
      const roles: string[] = JSON.parse(rolesString);
      return roles.includes(role);
    }
    return false;
  }

  /**
   * Clears all token and role information from local storage.
   */
  clearToken(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('token');
      localStorage.removeItem('is_admin');
      localStorage.removeItem('roles');
    }

    this.router.navigate(['/auth/login']);
  }

  /**
   * Retrieves the token from local storage.
   * @returns The token as a string or null.
   */
  getToken(): string | null {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('token');
    } else {
      return null;
    }
  }
}